import { Page } from '@app/components/page/Page';
import { Report } from '@mi-front/core';
import { ComponentType } from 'react';
import { ReportCtxProvider } from '../report-ctx';
import { ReportResultProps, ReportType } from '../types';
import { FormLibrary, ReportForm } from './ReportForm';
import { useReport } from './useReport';

export type ReportQueryParamsTransform<R extends ReportType> = {
  [K in keyof R['Values']]?: {
    parse?: (value: string) => R['Values'][K];
    serialize?: (value: NonNullable<R['Values'][K]>) => string;
  };
};

export interface ReportViewProps<R extends ReportType> {
  defaultValues?: R['Values'];
  queryParamsTransform?: ReportQueryParamsTransform<R>;
  download?:
    | false
    | {
        local?: boolean;
        resultToExport?: (result: R['Result']) => unknown[];
        filename: string | ((result: Report) => string);
        remote?: (values: R['Values'], format?: string) => Promise<unknown>;
      };
  reportFn: (values: R['Values'], format?: string) => Promise<R['Result']>;
  runOnLoad?: boolean;
  Form: ComponentType;
  FormActions?: ComponentType;
  Result: ComponentType<ReportResultProps<R>>;
  sort?: { id: string; desc: boolean };
  formLibrary?: FormLibrary;
  /** Optional. When not provided, count is derived from result.total when present. Pass to override or to show count for custom result shapes. */
  getResultCount?: (result: R['Result']) => number;
  ResultWrapper?: ComponentType<{ children: React.ReactNode }>;
}

/**
 * Report component for reports and non paginated listings.
 */
export function ReportView<R extends ReportType>({
  defaultValues,
  reportFn,
  download,
  queryParamsTransform = {},
  runOnLoad,
  Form,
  FormActions,
  Result,
  sort,
  formLibrary,
  getResultCount,
  ResultWrapper = Page.Content,
}: ReportViewProps<R>) {
  const ctx = useReport({
    defaultValues,
    reportFn,
    download,
    queryParamsTransform,
    runOnLoad,
    sort,
    getResultCount,
  });

  return (
    <ReportCtxProvider value={ctx}>
      <>
        <ReportForm Actions={FormActions} formLibrary={formLibrary}>
          <Form />
        </ReportForm>
        {ctx.result && (
          <ResultWrapper>
            <Result values={ctx.values} result={ctx.result} isLoading={ctx.isLoading} />
          </ResultWrapper>
        )}
      </>
    </ReportCtxProvider>
  );
}
